// DS: MO0.4
import React, { useContext } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { Url, USER_PROFILE_SHOP_SUB_PATHS } from '@wix/da-url';
import { PapiDeviation, RelatedBlock } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { OverlayContextProvider } from '@wix/da-react-context/pkg/OverlayContext';
import { useMeasureElementCookie } from '@wix/da-hooks/pkg/useMeasureElement/useMeasureElementCookie';
import BuyButton from '@wix/da-ds/pkg/Buttons/BuyButton';
import SaleIcon from '@wix/da-ds/pkg/Icons/16x16/Sale';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import {
  BiData,
  BuyCoreButtonBiEvent,
  ViewAdoptableTabBiEvent,
  ViewCommissionTabBiEvent,
  ViewPremiumGalleriesTabBiEvent,
  ViewSubscriptionTabBiEvent,
  ViewShopPremiumDownloadsTabBiEvent,
} from '@wix/da-bi/pkg/events';
import { normalizePremiumFolderData } from '../../../../utils/normalizePremiumFolderData';
import FolderShopCard from '../../../../ShopCard/FolderShopCard';
import { ResourceType } from '../../../../types/resource';
import UserAvatar from '../../../../User/Avatar';
import { DeviationTypes } from '../../../../types/deviation';
import TierPromotionCard from '../../../../DeviationViews/TierPromotionCard';
import AdoptableShopCard from '../../../../ShopCard/AdoptableShopCard';
import { getDeviationEntityId } from '../../../../redux/normalizr/schemas/deviation';
import BuyCoreBrandedButton from '../../../../Button/core/dsPresets/BuyCoreBrandedButton';
import { getDeviationResourceType } from '../../../../DeviationViews/_helpers';
import PremiumDownloadPromo from '../PremiumDownloadPromo';

import s from './MonetizationBlock.scss';

interface Props {
  block: RelatedBlock;
  deviation: PapiDeviation;
  className?: string;
  biSectionPrefix?: string;
}

const DEFAULT_CARD_WIDTH = 308;
const MOBILE_PADDING = parseInt(s['mobile-padding'], 10);

export const MonetizationBlock: React.FC<Props> = ({
  block: {
    contentType,
    collections: relatedCollections,
    gallections: relatedGalleries,
    deviations: relatedDeviations,
  },
  deviation,
  className,
  biSectionPrefix,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const biSectionName = `${biSectionPrefix ?? ''}${contentType}`;

  const { ref: containerRef, dimensions: measuredContainer } =
    useMeasureElementCookie(undefined, {
      cookieDim: 'width',
    });

  const cardWidthMobile = measuredContainer
    ? measuredContainer.width - MOBILE_PADDING * 2
    : DEFAULT_CARD_WIDTH;

  if (
    (contentType === 'monetization_promotion_premium_gallery_firstparty' ||
      contentType === 'monetization_promotion_premium_gallery_thirdparty') &&
    relatedGalleries &&
    relatedGalleries.length
  ) {
    const relatedGallection = relatedGalleries[0].gallection;

    const { isPremium } = normalizePremiumFolderData(
      relatedGallection?.premiumFolderData
    );

    if (relatedGallection && isPremium) {
      return (
        <div className={c(className, s['root'])} ref={containerRef}>
          <BiLoggerContextProvider value={{ sectionname: biSectionName }}>
            <FolderShopCard
              width={isMobile ? cardWidthMobile : DEFAULT_CARD_WIDTH}
              folder={relatedGallection}
              withIndicators={false}
              withUnlockButton={true}
              linkToShop={true}
              anchor
            />
          </BiLoggerContextProvider>
        </div>
      );
    }
  }

  if (relatedDeviations && relatedDeviations.length) {
    const relatedDeviation = relatedDeviations[0];
    if (relatedDeviation.type === DeviationTypes.TIER) {
      const tierEntityId = getDeviationEntityId(relatedDeviation);
      const biData = BiData<ViewSubscriptionTabBiEvent>({
        evid: 547,
        typeid: relatedDeviation.typeId,
        itemid: relatedDeviation.deviationId,
        referral_info: ReferralType.SIDEBAR_PROMOTION,
        page_itemid: deviation.deviationId,
        page_typeid: getDeviationResourceType(deviation),
        sectionname: biSectionName,
        section_typeid: ResourceType.DEVIATION,
      });
      return (
        <div
          ref={containerRef}
          className={c(className, s['root'], s['tier-card-root'])}
        >
          <BiLink
            href={Url.userSubscriptionsDeeplink({
              username: relatedDeviation!.author!.username,
              tierEntityId,
              anchor: true,
            })}
            className={s['link']}
            biData={biData}
          />
          <TierPromotionCard
            width={isMobile ? cardWidthMobile : DEFAULT_CARD_WIDTH}
            verticalLayout
            deviation={relatedDeviation}
          />
        </div>
      );
    } else if (relatedDeviation.isAdoptable) {
      return (
        <div className={c(className, s['root'])} ref={containerRef}>
          <BiLoggerContextProvider value={{ sectionname: biSectionName }}>
            <OverlayContextProvider
              value={{ showThumbStatusIndicators: false }}
            >
              <AdoptableShopCard
                width={isMobile ? cardWidthMobile : DEFAULT_CARD_WIDTH}
                deviation={relatedDeviation}
                overrideHref={Url.userShopLink(
                  relatedDeviation.author!.username,
                  USER_PROFILE_SHOP_SUB_PATHS.EXCLUSIVES,
                  {
                    deviationid: `${
                      contentType !== 'monetization_promotion_adoptable_generic'
                        ? relatedDeviation.deviationId
                        : deviation.deviationId
                    }`,
                  },
                  true
                )}
                footerSlot={
                  <BuyButton
                    isFakeButton
                    tabIndex={-1}
                    icon={SaleIcon}
                    size="medium"
                    fillContainerWidth
                    rounded
                  >
                    {t('sidebar.monetizationBlock.var.exclusive.CTA')}
                  </BuyButton>
                }
              />
            </OverlayContextProvider>
          </BiLoggerContextProvider>
        </div>
      );
    }
  }

  let type:
    | 'tier'
    | 'commission'
    | 'premium_gallery'
    | 'adoptable'
    | 'pcp'
    | 'core';
  let link: string;
  let biData;
  const commonBiData = {
    typeid: deviation.typeId,
    itemid: deviation.deviationId,
    referral_info: ReferralType.SIDEBAR_PROMOTION,
    sectionname: biSectionName,
    section_typeid: ResourceType.DEVIATION,
  };
  if (
    contentType === 'monetization_promotion_tier' ||
    contentType === 'monetization_promotion_tier_firstparty' ||
    contentType === 'monetization_promotion_tier_generic' ||
    contentType === 'monetization_promotion_tier_thirdparty' ||
    contentType === 'subscription_promotion' /* backwards compatibility */
  ) {
    type = 'tier';
    link = Url.userSubscriptionsPath({
      username: deviation.author!.username,
      anchor: true,
    });
    biData = BiData<ViewSubscriptionTabBiEvent>({
      evid: 547,
      ...commonBiData,
    });
  } else if (
    contentType === 'monetization_promotion_adoptable' ||
    contentType === 'monetization_promotion_adoptable_firstparty' ||
    contentType === 'monetization_promotion_adoptable_thirdparty' ||
    contentType === 'monetization_promotion_adoptable_generic'
  ) {
    type = 'adoptable';
    link = Url.userShopLink(
      deviation.author!.username,
      USER_PROFILE_SHOP_SUB_PATHS.EXCLUSIVES,
      {},
      true
    );
    biData = BiData<ViewAdoptableTabBiEvent>({
      evid: 546,
      ...commonBiData,
    });
  } else if (
    contentType === 'monetization_promotion_premium_gallery' ||
    contentType === 'monetization_promotion_premium_gallery_generic'
  ) {
    type = 'premium_gallery';
    link = Url.userShopLink(
      deviation.author!.username,
      USER_PROFILE_SHOP_SUB_PATHS.PREMIUM_FOLDERS,
      {},
      true
    );
    biData = BiData<ViewPremiumGalleriesTabBiEvent>({
      evid: 549,
      ...commonBiData,
    });
  } else if (
    contentType === 'monetization_promotion_commission' ||
    contentType === 'monetization_promotion_commission_generic'
  ) {
    type = 'commission';
    link = Url.userShopLink(
      deviation.author!.username,
      USER_PROFILE_SHOP_SUB_PATHS.COMMISSIONS,
      {},
      true
    );
    biData = BiData<ViewCommissionTabBiEvent>({
      evid: 548,
      ...commonBiData,
    });
  } else if (
    contentType === 'monetization_promotion_pcp_firstparty' ||
    contentType === 'monetization_promotion_pcp_thirdparty'
  ) {
    const firstDeviation = (relatedDeviations || []).find(Boolean);
    const authorUsername = firstDeviation?.author?.username;
    const deviationId = firstDeviation?.deviationId;
    const shopPremiumDownloadsUrl =
      authorUsername && deviationId
        ? Url.userShopLink(
            authorUsername,
            USER_PROFILE_SHOP_SUB_PATHS.PREMIUM_CONTENT,
            { deviationid: deviationId.toString() },
            true
          )
        : undefined;

    return (
      <div className={c(className, s['root'])} ref={containerRef}>
        <BiLoggerContextProvider value={{ sectionname: biSectionName }}>
          <PremiumDownloadPromo
            deviations={relatedDeviations}
            width={isMobile ? cardWidthMobile : DEFAULT_CARD_WIDTH}
            overrideHref={shopPremiumDownloadsUrl}
          />
        </BiLoggerContextProvider>
      </div>
    );
  } else if (
    contentType === 'monetization_promotion_pcp' ||
    contentType === 'monetization_promotion_pcp_generic'
  ) {
    type = 'pcp';
    link = Url.userShopLink(
      deviation.author!.username,
      USER_PROFILE_SHOP_SUB_PATHS.PREMIUM_CONTENT,
      {},
      true
    );
    biData = BiData<ViewShopPremiumDownloadsTabBiEvent>({
      evid: 557,
      ...commonBiData,
    });
  } else if (contentType === 'monetization_promotion_core_generic') {
    type = 'core';
    link = Url.coreMembershipLink();
    biData = BiData<BuyCoreButtonBiEvent>({
      evid: 375,
      ...commonBiData,
    });
  } else {
    return null;
  }

  if (!deviation.author) {
    return null;
  }

  if (type === 'core') {
    return (
      <div className={c(className, s['root'], s['core-upsell'])}>
        <div className={s['heading']}>
          {t(`sidebar.monetizationBlock.var.core.heading`)}
        </div>
        <div className={s['text']}>
          {t(`sidebar.monetizationBlock.var.core.body`)}
        </div>
        <BuyCoreBrandedButton size="medium" biData={biData}>
          {t(`sidebar.monetizationBlock.var.core.CTA`)}
        </BuyCoreBrandedButton>
      </div>
    );
  }

  return (
    <div className={c(className, s['root'])}>
      <div className={c(s['content'], s[`type-${type}`])}>
        <UserAvatar
          className={s['avatar']}
          user={deviation.author}
          withTooltip={false}
          size={40}
          relatedItemBiData={{
            typeid: deviation.typeId,
            itemid: deviation.deviationId,
          }}
        />
        <div className={s['text']}>
          {t(`sidebar.monetizationBlock.var.${type}.body`)}
        </div>

        <BiLink className={s['btn-subscribe']} href={link} biData={biData}>
          {t(`sidebar.monetizationBlock.var.${type}.CTA`)}
        </BiLink>
      </div>
    </div>
  );
};
MonetizationBlock.displayName = 'MonetizationBlock';

export default MonetizationBlock;
